import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
  AfterViewInit,
  OnDestroy,
  HostListener,
  Type,
  NgZone
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { AuthService, Logger } from '@core/services';
import { LegalService } from '@core/services/legal/legal.service';
import { EulaComponent } from './eula/eula.component';
import { PrivacyPolicyComponent } from './privacy-policy/privacy-policy.component';
import { animate, style, transition, trigger } from '@angular/animations';

const log = new Logger('LegalModalComponent');

@Component({
  selector: 'app-legal-modal',
  templateUrl: './legal-modal.component.html',
  styleUrls: ['./legal-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('fadeSwitch', [
      transition('* => *', [style({ opacity: 0 }), animate('200ms ease-in', style({ opacity: 1 }))])
    ])
  ]
})
export class LegalModalComponent implements AfterViewInit, OnDestroy {
  @Input() showAcceptButton = false;
  @Input() activeSection: 'eula' | 'privacy' = 'eula';
  @Output() termsAccepted = new EventEmitter<boolean>();

  @ViewChild('termsSection') termsSectionRef!: ElementRef<HTMLElement>;

  showModal = false;
  canAccept = false;
  scrollPercent = 0;

  private scrollCompletionMap: Record<'eula' | 'privacy', boolean> = {
    eula: false,
    privacy: false
  };

  get sectionComponent(): Type<any> {
    return this.activeSection === 'eula' ? EulaComponent : PrivacyPolicyComponent;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private legalService: LegalService,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          this.termsSectionRef?.nativeElement?.focus();
          this.canAccept = this.scrollCompletionMap[this.activeSection];
          log.debug(
            `Modal view initialized: section=${this.activeSection}, canAccept=${this.canAccept}`
          );
        });
      }, 0);
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  handleEscapeKey(event: KeyboardEvent): void {
    if (this.showModal) {
      log.info('Escape key pressed: closing modal');
      this.close();
    }
  }

  onScroll(container: HTMLElement): void {
    const { scrollTop, scrollHeight, clientHeight } = container;
    this.scrollPercent = (scrollTop / (scrollHeight - clientHeight)) * 100;
    log.debug(
      `Scroll progress: section=${this.activeSection}, percent=${this.scrollPercent.toFixed(1)}%`
    );

    if (this.scrollPercent >= 99 && !this.scrollCompletionMap[this.activeSection]) {
      this.scrollCompletionMap[this.activeSection] = true;
      log.info(`User reached bottom of ${this.activeSection}, enabling Accept`);
    }

    this.canAccept = this.scrollCompletionMap[this.activeSection];
  }

  accept(): void {
    log.info(`User accepted terms: section=${this.activeSection}`);
    this.legalService
      .acceptTerms()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.termsAccepted.emit(true);
        this.showModal = false;
        log.debug('Modal closed after accept');
      });
  }

  close(): void {
    log.info('Modal manually closed');
    this.showModal = false;
  }

  async decline(): Promise<void> {
    log.info('User declined terms');
    this.showModal = false;

    if (this.showAcceptButton) {
      log.info('Signing out user due to declined terms');
      await this.authService.signOut(true);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    log.debug('LegalModalComponent destroyed');
  }
}
